// ----------------------------------------------------------------------
// The printable QR cards of the restaurant's QR sheet, in sheet order.
// Ported from admin-app (src/components/restaurant-detail/qrSheet.ts +
// the qrUrl / tablesByZone helpers of orderQr.ts).
// ----------------------------------------------------------------------

import type { QrTable, RestaurantQrCodes } from '../../models/qr-codes';
import type { QrCardSpec } from './qr-card';

export type QrGroup = 'togo' | 'dining' | 'dine';

/** One printable QR of the sheet. */
export interface SheetCard {
  /** "togo", "td:<id>" or "di:<table id>". */
  key: string;
  group: QrGroup;
  /** Tile caption: "To-Go", "Table 7", "Patio 1". */
  label: string;
  /** Second line of the tile: "To-Go · TG", "To-Go dining · TD-7", "Dine-in · 4 seats". */
  caption: string;
  /** Zone the dine-in table sits in ('' when it has none); unused for the others. */
  zone: string;
  spec: QrCardSpec;
}

/** A real image URL, or null for empty / "null" strings. */
export function qrUrl(value: unknown): string | null {
  const text = String(value ?? '').trim();
  return text && text !== 'null' ? text : null;
}

export const zoneName = (t: QrTable) => (t.zone?.zone_name ?? '').trim();

/** Tables grouped by zone, zones and tables in name order, tables without a zone last. */
export function tablesByZone(tables: QrTable[]): [string, QrTable[]][] {
  const groups = new Map<string, QrTable[]>();
  for (const t of tables) {
    const z = zoneName(t);
    groups.set(z, [...(groups.get(z) ?? []), t]);
  }
  const byName = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase());
  const zones = [...groups.keys()].filter(Boolean).sort(byName);
  if (groups.has('')) zones.push('');
  return zones.map((z) => [
    z,
    [...groups.get(z)!].sort((a, b) => byName(a.table_name ?? '', b.table_name ?? '')),
  ]);
}

const seats = (n: number | null | undefined) =>
  n == null ? '' : ` · ${n} ${n === 1 ? 'seat' : 'seats'}`;

/** Every printable QR, in sheet order: To-Go, To-Go dining tables, dine-in by zone. */
export function sheetCards(
  sheet: RestaurantQrCodes | null | undefined,
  restaurantName: string,
  logoUrl?: string | null,
): SheetCard[] {
  if (!sheet) return [];
  const base = { restaurantName, logoUrl };
  const cards: SheetCard[] = [];
  const togo = qrUrl(sheet.togo?.qr_code_path);
  if (togo) {
    cards.push({
      key: 'togo',
      group: 'togo',
      label: 'Pick-up counter',
      caption: 'To-Go · TG',
      zone: '',
      spec: {
        ...base,
        kind: 'togo',
        label: '',
        qrUrl: togo,
        togoDineIn: sheet.order_types?.togo_dine_in,
      },
    });
  }
  for (const t of sheet.togo_tables ?? []) {
    const url = qrUrl(t.qr_code_path);
    if (!url || t.status !== 'active') continue;
    cards.push({
      key: `td:${t.togo_table_id}`,
      group: 'dining',
      label: `Table ${t.table_number}`,
      caption: `To-Go dining · TD-${t.table_number}`,
      zone: '',
      spec: { ...base, kind: 'dining', label: String(t.table_number), qrUrl: url },
    });
  }
  for (const [zone, tables] of tablesByZone(sheet.tables ?? [])) {
    for (const t of tables) {
      const url = qrUrl(t.qr_code_path);
      if (!url) continue;
      const name = t.table_name?.trim() || 'Table';
      cards.push({
        key: `di:${t.table_id}`,
        group: 'dine',
        label: name,
        caption: `Dine-in${seats(t.number_of_seats)}`,
        zone,
        spec: { ...base, kind: 'dine', label: name, qrUrl: url },
      });
    }
  }
  return cards;
}

/** Dine-in cards bucketed by zone, in sheet order; the no-zone bucket is last. */
export function cardsByZone(cards: SheetCard[]): { zone: string; cards: SheetCard[] }[] {
  const out: { zone: string; cards: SheetCard[] }[] = [];
  for (const card of cards) {
    if (card.group !== 'dine') continue;
    const last = out[out.length - 1];
    if (last && last.zone === card.zone) last.cards.push(card);
    else out.push({ zone: card.zone, cards: [card] });
  }
  return out;
}

export const GROUP_NAME: Record<QrGroup, { one: string; many: string }> = {
  togo: { one: 'To-Go card', many: 'To-Go cards' },
  dining: { one: 'To-Go dining card', many: 'To-Go dining cards' },
  dine: { one: 'dine-in card', many: 'dine-in cards' },
};

/** "1 card" / "12 cards". */
export const cardCount = (n: number) => `${n} ${n === 1 ? 'card' : 'cards'}`;
